import React from 'react'
import cn from 'classnames'
import { Typography } from '@grammarly/badger-ui'

import { Themes, useIsSectionThemeDark } from '../../../contexts/section-theme-context'
import { TypeSectionTrustedBy } from '../../../lib/types'
import { TrustedByItem } from '../../item/trusted-by'
import { PageSection } from '../../page-section'
import type { ContentProps } from '../../renderer'

import typographyStyles from '../../ui-lib/typography/typography.module.scss'
import styles from './trusted-by.module.scss'
import { ImageScroller } from './ImageScroller'
import { renderRichTextMultiLineContent } from '../../renderer/rich-text'
import { getThemeFromBackgroundColor } from '../../page-section/theme'
import { Background } from '../../../lib/types/background'
import { useIsCompactSections } from '../../../contexts/compact-sections-context'
import { usePageTheme } from '../../../contexts/page-theme-context'

const { C4, C1 } = Typography

const TrustedByListDescription: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const isDarkTheme = useIsSectionThemeDark()

  return (
    <C4 as="div" className={isDarkTheme ? typographyStyles.colorNeutral10 : typographyStyles.colorNeutral50}>
      {children}
    </C4>
  )
}

const TrustedByHeader: React.FC<{ background?: Background; children?: React.ReactNode }> = ({
  children,
  background,
}) => {
  const { color } = usePageTheme()
  const isDarkTheme = getThemeFromBackgroundColor(background?.color, color === 'convertTo2024') === Themes.Dark
  const typographyStyle = isDarkTheme
    ? typographyStyles.colorNeutral10
    : color === 'convertTo2024'
      ? typographyStyles.color24Neutral80
      : typographyStyles.colorNeutral50

  return (
    <C1 as="div" className={typographyStyle}>
      {children}
    </C1>
  )
}

const TrustedBySectionContent: React.FC<ContentProps<TypeSectionTrustedBy>> = (props) => {
  const {
    structure: {
      fields: { listDescription, list, enableScroll, numberOfRows, background },
    },
  } = props

  if (!list) {
    return null
  }

  return (
    <div className={styles.logosContainer}>
      {enableScroll && numberOfRows ? (
        <>
          {listDescription && (
            <TrustedByListDescription>
              {listDescription}
              {
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                ':'
              }
            </TrustedByListDescription>
          )}
          <ImageScroller list={list} numberOfRows={numberOfRows} background={background?.color} />
        </>
      ) : (
        <>
          {listDescription && (
            <TrustedByListDescription>
              {listDescription}
              {
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                ':'
              }
            </TrustedByListDescription>
          )}
          {list.map((item) => (
            <TrustedByItem key={item.sys.id} structure={item} />
          ))}
        </>
      )}
    </div>
  )
}

export const TrustedBySection: React.FC<ContentProps<TypeSectionTrustedBy>> = ({ className, structure }) => {
  const {
    sys,
    fields: { internalName, background, heading, enableScroll },
  } = structure

  const isCompactSections = useIsCompactSections()

  return (
    <PageSection
      internalName={internalName}
      sysStructure={sys}
      className={className}
      heading={isCompactSections ? undefined : heading}
      background={background}
    >
      {isCompactSections && heading?.fields.title && (
        <div className={cn(enableScroll ? styles.scrollingHeader : styles.nonScrollingHeader)}>
          <TrustedByHeader background={background}>
            {renderRichTextMultiLineContent(heading.fields.title)}
          </TrustedByHeader>
        </div>
      )}
      <TrustedBySectionContent structure={structure} />
    </PageSection>
  )
}
